'use client';
import { useMemo, useState } from 'react';
import { useParams } from 'next/navigation';

import {
  useIsMobile,
  useLiveSportsSocket,
  useMarketTradeModal,
  useSportsMarketSocket,
} from '@/hooks';
import { useEventBookmarkAction } from '@/hooks';
import { useSportsById } from '@/services/query/sports.queries';
import { EventMarket } from '@/types';
import {
  findMarket,
  findMarketByLine,
  getOutcomes,
  isTennisGame as checkIsTennisGame,
  sortMarketTabs,
} from '@/utils';

import { ROUTES } from '@/constants';

export const useLiveGameDetail = () => {
  const params = useParams();
  const id = params?.id as string;

  const [selectedSide, setSelectedSide] = useState<'yes' | 'no'>('yes');
  const [sidebarOutcomeIndex, setSidebarOutcomeIndex] = useState<0 | 1>(0);
  const [selectedMarket, setSelectedMarket] = useState<EventMarket | null>(null);
  const [selectedMarketType, setSelectedMarketType] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('');
  const [sidebarTab, setSidebarTab] = useState<'buy' | 'sell'>('buy');
  const { checkIsBookmarked, handleBookmarkToggle } = useEventBookmarkAction();

  const { data: rawGame, isLoading } = useSportsById(id);

  const { liveData } = useLiveSportsSocket();

  // Combine events to feed into the socket hook for subscription
  const initialEvents = useMemo(() => (rawGame ? [rawGame] : []), [rawGame]);
  useSportsMarketSocket(initialEvents as any);

  const game = useMemo(() => {
    if (!rawGame || !rawGame.gameId) return rawGame;
    const socketUpdate = liveData[rawGame.gameId.toString()];
    if (!socketUpdate) return rawGame;

    return {
      ...rawGame,
      score: socketUpdate.score || rawGame.score,
      period: socketUpdate.period || rawGame.period,
      elapsed: socketUpdate.elapsed || rawGame.elapsed,
      isLive: socketUpdate.live ?? rawGame.isLive,
    };
  }, [rawGame, liveData]);

  const isTennisGame = useMemo(() => checkIsTennisGame(game), [game]);

  // Derive markets and outcomes from the game's markets array
  const moneylineMarket = useMemo(() => (game ? findMarket(game, 'moneyline') : undefined), [game]);

  // Tabs and Market Grouping
  const tabs = useMemo(() => {
    if (!game?.markets) return [];
    if (game.uiPattern === 'single-event-flat') return [];
    const rawLabels = (game.marketGroups || []).map((g) => g.label);
    return sortMarketTabs(rawLabels);
  }, [game]);

  // Initialize active tab from API data if not already set
  const apiActiveTab = useMemo(() => {
    return game?.marketGroups?.find((g) => g.isCurrentTab)?.label;
  }, [game]);

  const effectiveTab = useMemo(() => {
    if (activeTab) return activeTab;

    // 1. Look for explicit 'current' flag from API
    if (apiActiveTab) return apiActiveTab;

    // 2. Fallback: Prioritize 'Game Lines' or principal markets in derived tabs
    const gameLinesTab = tabs.find((t) => (t || '').toLowerCase().includes('game line'));
    if (gameLinesTab) return gameLinesTab;

    // 3. Last fallback: first available tab
    return tabs.length > 0 ? tabs[0] : '';
  }, [activeTab, apiActiveTab, tabs]);

  // Handle outcome click → open sidebar
  const sidebarData = useMemo(() => {
    if (!game) return null;

    let marketToUse: EventMarket | undefined = undefined;
    if (selectedMarket && game?.markets) {
      marketToUse = game.markets.find((m) => String(m.id) === String(selectedMarket.id));
    }
    if (!marketToUse) {
      marketToUse = selectedMarket || undefined;
    }

    // Fallback if no specific market selected or found
    if (!marketToUse) {
      marketToUse =
        moneylineMarket ||
        game?.markets?.find((m) => m.sportsMarketType?.toLowerCase() === 'moneyline') ||
        game?.markets?.[0];
    }

    if (!marketToUse) return null;

    const outcomes = getOutcomes(marketToUse);
    const outcome = outcomes[sidebarOutcomeIndex] || outcomes[0];
    if (!outcome) return null;

    return {
      market: {
        ...marketToUse,
        title: game?.title || '',
        outcomes: outcomes.map((o) => ({
          ...o,
          // Keep trade sidebar/modal aligned with cards: prefer orderbook ask.
          price:
            typeof o.bestAsk === 'number'
              ? o.bestAsk
              : typeof o.price === 'string'
                ? parseFloat(o.price)
                : o.price || 0,
        })),
      },
      outcome: {
        ...outcome,
        price:
          typeof outcome.bestAsk === 'number'
            ? outcome.bestAsk
            : typeof outcome.price === 'string'
              ? parseFloat(outcome.price)
              : outcome.price || 0,
      },
    };
  }, [game, moneylineMarket, selectedMarket, sidebarOutcomeIndex]);

  // Handle outcome click → update sidebar index
  const handleOutcomeClick = (
    market: EventMarket | undefined,
    outcomeIndex: 0 | 1,
    marketType: string
  ) => {
    if (!market) return;
    setSidebarOutcomeIndex(outcomeIndex);
    setSelectedSide(outcomeIndex === 0 ? 'yes' : 'no');
    setSelectedMarketType(marketType);
    setSelectedMarket(market);
  };

  const isMobile = useIsMobile();
  const { handleOpenTradeModal, tradeModalState, handleCloseTradeModal } = useMarketTradeModal();

  const handleTradeClick = (
    marketId: string,
    outcomeId: string,
    marketType: string,
    isBuy: boolean = true,
    shouldOpenModal: boolean = true
  ) => {
    const currentMarket = game?.markets?.find((m) => String(m.id) === String(marketId));
    if (currentMarket) {
      const outcomes = getOutcomes(currentMarket);
      const idx = outcomes.findIndex((o) => o.id === outcomeId);
      const selectedOutcome = outcomes[idx] || outcomes[0];

      handleOutcomeClick(currentMarket, (idx === 1 ? 1 : 0) as 0 | 1, marketType);

      if (isMobile && selectedOutcome && shouldOpenModal) {
        handleOpenTradeModal(currentMarket, selectedOutcome, isBuy);
      }
    }
  };

  const handleLineChange = (line: string | number, marketType: string, marketId?: string) => {
    if (!game?.markets) return;
    const newMarket = marketId
      ? game.markets.find((m) => String(m.id) === String(marketId))
      : findMarketByLine(game as any, marketType, line);
    if (newMarket) {
      setSelectedMarket(newMarket as EventMarket);
      setSelectedMarketType(marketType);
    }
  };

  const handleBookmarkClick = (e?: React.MouseEvent) => {
    if (!game) return;
    handleBookmarkToggle(e, game as any, 'sport', ROUTES.MARKET_DETAILS(game.id));
  };

  const isBookmarked = game ? checkIsBookmarked(game.id) : false;

  const liveTradeModalState = useMemo(() => {
    if (!tradeModalState.market || !tradeModalState.outcome || !game?.markets) {
      return tradeModalState;
    }
    const updatedMarket = game.markets.find(
      (m) => String(m.id) === String(tradeModalState.market?.id)
    );
    if (!updatedMarket) return tradeModalState;

    const outcomes = getOutcomes(updatedMarket);
    const updatedOutcome = outcomes.find(
      (o) => String(o.id) === String(tradeModalState.outcome?.id)
    );
    if (!updatedOutcome) {
      return {
        ...tradeModalState,
        market: updatedMarket,
      };
    }

    return {
      ...tradeModalState,
      market: updatedMarket,
      outcome: updatedOutcome,
    };
  }, [tradeModalState, game]);

  return {
    state: {
      game,
      isLoading,
      activeTab: effectiveTab,
      tabs,
      sidebarData,
      selectedSide,
      sidebarOutcomeIndex,
      selectedMarketType,
      activeOutcomeId: sidebarData?.outcome?.id || null,
      tradeModalState: liveTradeModalState,
      sidebarTab,
      isTennisGame,
      isBookmarked,
      uiPattern: game?.uiPattern,
      relatedTabs: game?.relatedTabs,
    },
    handlers: {
      setActiveTab,
      handleOutcomeClick,
      setSelectedSide,
      handleBookmarkClick,
      setSidebarTab,
      handleTradeClick,
      handleLineChange,
      handleCloseTradeModal,
    },
  };
};
